import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { LanguageSwitcher } from "../../locales/i18n";

import SideNav from "./SideNav";

import { mdiAccount, mdiLogout } from "@mdi/js";
import Icon from "@mdi/react";
import MenuIcon from "@mui/icons-material/Menu";
import { Box } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useUser } from "../../contexts/UserContext";

interface NavBarProps {
  showModeratorBoard: boolean;
  showAdminBoard: boolean;
  logOut: () => void;
  drawerWidth: number;
}

const NavBar: React.FC<NavBarProps> = ({
  showModeratorBoard,
  showAdminBoard,
  logOut,
  drawerWidth,
}) => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const { user, userId } = useUser();

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const renderNavigationButton = (
    to: string,
    id: string,
    defaultMessage: string
  ) => (
    <Button sx={{ my: 2, color: "white", display: "block" }} id={`nav-${id}-link`}>
      <Link to={to} className="nav-link">
        <FormattedMessage id={id} defaultMessage={defaultMessage} />
      </Link>
    </Button>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: userId
            ? { sm: `calc(100% - ${drawerWidth}px)` }
            : "100%",
          ml: userId ? { sm: `${drawerWidth}px` } : 0,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            <Typography
              variant="h4"
              noWrap
              component={Link}
              to="/home"
              sx={{
                mr: 2,
                display: { xs: "none", sm: "flex" },
                fontFamily: "default",
                fontSize: { sm: 20, md: 35 },
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              Kulmasohva 3
            </Typography>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                mr: 1,
                display: { xs: "flex", sm: "none" },
                ...(mobileOpen && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h4"
              noWrap
              component={Link}
              to="/home"
              sx={{
                mr: 2,
                display: { xs: "flex", sm: "none" },
                fontSize: { xs: 12, sm: 15 },
                flexGrow: 1,
                fontWeight: 700,
                letterSpacing: ".3rem",
                color: "inherit",
                textDecoration: "none",
              }}
            >
              Kulmasohva 3
            </Typography>
            <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "flex" } }}>
              {" "}
              {/* Start Top Nav */}
              {showAdminBoard &&
                renderNavigationButton("/admin", "admin", "Admin Board")}
              {showModeratorBoard &&
                renderNavigationButton("/mod", "mod", "Mod Board")}
              {userId ? (
                <>
                  {renderNavigationButton("/app", "user", "Dashboard")}
                  {renderNavigationButton("/contact", "contact", "Contact Us")}
                </>
              ) : (
                <>
                  {renderNavigationButton("/login", "login", "Log In")}
                  {renderNavigationButton("/register", "signup", "Sign Up")}
                </>
              )}
            </Box>
            {userId && (
              <Box sx={{ flexGrow: 0, mr: "45px" }} id="userMenu">
                {" "}
                {/* Start Avatar or User icon */}
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar
                      alt={user?.username || "User"}
                      src="/static/images/avatar/2.jpg"
                    />
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px", mr: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={handleCloseUserMenu} id="nav-profile-link">
                    <Icon
                      path={mdiAccount}
                      size={1}
                      color="black"
                      style={{ marginRight: "5px" }}
                    />
                    <Typography textAlign="center">
                      <Link to={"/profile"} className="nav-link">
                        {user?.username}
                        <FormattedMessage id="profile" />
                      </Link>
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleCloseUserMenu} id="nav-logout-link">
                    <Icon
                      path={mdiLogout}
                      size={1}
                      color="black"
                      style={{ marginRight: "5px" }}
                    />

                    <Typography textAlign="center">
                      <Link to={"/login"} className="nav-link" onClick={logOut}>
                        <FormattedMessage id="log-out" />
                      </Link>
                    </Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}{" "}
            {/* End Avatar or User icon */}
            <LanguageSwitcher />
          </Toolbar>
        </Container>
      </AppBar>
      <SideNav
        menuOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        handleDrawerTransitionEnd={handleDrawerTransitionEnd}
        handleDrawerClose={handleDrawerClose}
        drawerWidth={drawerWidth}
        showAdminBoard={showAdminBoard}
      />
    </>
  );
};

export default NavBar;
